import { stickerUrl } from "@listou/shared/stickers";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { formatDay } from "../../lib/format";
import { addDays, addMonths, dating, pregnancyStart } from "../../lib/pregnancy";
import { DateTile, DISCLAIMER, ISO_DAY } from "./due-date";
import { ToolLayout, type ToolCopy } from "./tool-layout";

export const CONCEPTION_COPY: ToolCopy = {
  h1: "Calculer sa date de conception",
  intro:
    "À partir de vos dernières règles, de la date prévue d'accouchement ou de votre échographie, estimez le jour de la fécondation et la période où a eu lieu la conception.",
  sections: [
    {
      heading: "Comment est-elle estimée ?",
      paragraphs: [
        "La fécondation suit l'ovulation de quelques heures, et l'ovulation a lieu environ 14 jours avant les règles suivantes. Depuis le terme, on remonte de 9 mois, la durée d'une grossesse selon le calcul français.",
        "L'échographie du premier trimestre donne l'estimation la plus fiable : c'est elle qui fixe officiellement le début de grossesse.",
      ],
    },
    {
      heading: "Pourquoi une période et pas un jour ?",
      paragraphs: [
        "Les spermatozoïdes survivent jusqu'à cinq jours. Le rapport fécondant a donc pu avoir lieu jusqu'à cinq jours avant la fécondation : c'est la période affichée.",
      ],
    },
  ],
  faq: [
    {
      question: "Peut-on connaître le jour exact de la conception ?",
      answer:
        "Rarement, sauf après une FIV ou avec un suivi d'ovulation. Le calcul donne une estimation à quelques jours près.",
    },
    {
      question: "L'échographie peut-elle dater la conception ?",
      answer:
        "Oui : entre 11 et 14 SA, la mesure du bébé date le début de grossesse à 3 à 5 jours près, la méthode la plus précise hors FIV.",
    },
  ],
};

type From = "lmp" | "due" | "scan";

const TABS: [From, string][] = [
  ["lmp", "Dernières règles"],
  ["due", "Date prévue d'accouchement"],
  ["scan", "Échographie"],
];

export function ConceptionView() {
  const [from, setFrom] = useState<From>("lmp");
  const [day, setDay] = useState("");
  const [cycle, setCycle] = useState("28");
  const [weeks, setWeeks] = useState("");
  const [extra, setExtra] = useState("0");

  const start = (() => {
    if (!ISO_DAY.test(day)) return null;
    if (from === "due") return addMonths(day, -9);
    if (from === "scan") {
      const w = Number(weeks);
      const d = Number(extra);
      if (weeks === "" || !Number.isInteger(w) || w < 5 || w > 42) return null;
      if (!Number.isInteger(d) || d < 0 || d > 6) return null;
      return pregnancyStart({ method: "ultrasound", scanDate: day, weeks: w, days: d });
    }
    const c = Number(cycle);
    if (!Number.isInteger(c) || c < 21 || c > 45) return null;
    return pregnancyStart({ method: "lmp", lmp: day, cycleDays: c });
  })();

  return (
    <ToolLayout
      copy={CONCEPTION_COPY}
      eyebrow="Calculateur gratuit"
      stickers={[stickerUrl("bebe", "cigogne"), stickerUrl("bebe", "hochet")]}
      disclaimer={DISCLAIMER}
    >
      <div className="flex flex-wrap justify-center gap-1">
        {TABS.map(([mode, label]) => (
          <button
            key={mode}
            type="button"
            className={from === mode ? "btn btn-sm" : "btn-ghost btn-sm"}
            onClick={() => setFrom(mode)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className={`label ${from === "due" ? "sm:col-span-2" : ""}`}>
          {from === "lmp"
            ? "Premier jour des dernières règles"
            : from === "due"
              ? "Date prévue d'accouchement"
              : "Date de l'échographie"}
          <input
            type="date"
            className="input"
            value={day}
            onChange={(event) => setDay(event.target.value)}
          />
        </label>
        {from === "lmp" && (
          <label className="label">
            Durée du cycle (jours)
            <input
              type="number"
              min={21}
              max={45}
              className="input"
              value={cycle}
              onChange={(event) => setCycle(event.target.value)}
            />
          </label>
        )}
        {from === "scan" && (
          <div className="grid grid-cols-2 gap-3">
            <label className="label">
              SA
              <input
                type="number"
                min={5}
                max={42}
                className="input"
                value={weeks}
                onChange={(event) => setWeeks(event.target.value)}
              />
            </label>
            <label className="label">
              Jours
              <input
                type="number"
                min={0}
                max={6}
                className="input"
                value={extra}
                onChange={(event) => setExtra(event.target.value)}
              />
            </label>
          </div>
        )}
      </div>
      {start ? (
        <div className="grid gap-6 border-t-[1.5px] border-line pt-6" aria-live="polite">
          <div className="grid justify-items-center gap-1 text-center">
            <p className="font-bold text-muted">Date de conception estimée</p>
            <p className="font-soft text-4xl font-medium text-accent-text sm:text-5xl">
              {formatDay(start)}
            </p>
            <p className="text-ink/80">
              Rapport fécondant probable entre le {formatDay(addDays(start, -5))} et le{" "}
              {formatDay(start)}.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <DateTile
              label="Date prévue d'accouchement (41 SA)"
              value={formatDay(dating({ method: "conception", conception: start }).dueDate)}
            />
            <DateTile
              label="Semaines d'aménorrhée comptées depuis le"
              value={formatDay(addDays(start, -14))}
            />
          </div>
          <p className="text-center">
            <Link
              to="/calendrier-chinois-grossesse"
              className="font-bold text-accent-text underline"
            >
              Fille ou garçon ? Essayez le calendrier chinois
            </Link>
          </p>
        </div>
      ) : (
        <p className="text-center text-muted">Le résultat s'affiche dès la date saisie.</p>
      )}
    </ToolLayout>
  );
}
